import LoginIcon from '@mui/icons-material/Login';
import get from 'lodash/get';
import { ReactElement, useState } from 'react';
import { Button, useGetIdentity, useNotify, useRecordContext } from 'react-admin';
import { LoginAsTarget, loginAsUrl, mintLoginAsToken } from '../lib/loginAs';

export interface LoginAsButtonProps {
  icon?: ReactElement;
  label?: string;
  /** the record is a profile (default: its `uuid`) */
  profileUuidSource?: string;
  /** the record is a user account: logs in on the profile it has selected */
  userAccountIdSource?: string;
  fullNameSource?: string;
}

export const LoginAsButton = (props: LoginAsButtonProps) => {
  const { icon = <LoginIcon />, label = 'Login As', profileUuidSource = 'uuid', userAccountIdSource, fullNameSource } = props;
  const record = useRecordContext(props);
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);

  if (!record) return null;
  const targetId = get(record, userAccountIdSource ?? profileUuidSource) as string | number | undefined;
  const target: LoginAsTarget | null = targetId == null || targetId === '' ? null : userAccountIdSource ? { userAccountId: targetId } : { profileUuid: String(targetId) };
  const formattedLabel = fullNameSource ? `${label} (${get(record, fullNameSource)})` : label;

  const openLoginTab = async () => {
    if (!target) return;
    // Opened before the request: a tab opened after an await is blocked as a popup.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    setLoading(true);
    try {
      const token = await mintLoginAsToken(target);
      const url = loginAsUrl(token, identity?.adminUUID as string | undefined);
      if (tab) tab.location.href = url;
      else window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      tab?.close();
      notify(`Login As failed: ${e instanceof Error ? e.message : String(e)}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={(event) => {
        // the button sits inside list rows that are links to the record; stay on this page
        event.preventDefault();
        event.stopPropagation();
        void openLoginTab();
      }}
      label={formattedLabel}
      disabled={loading || !target}
    >
      {icon}
    </Button>
  );
};
